import React, { useState } from "react";
import { Check } from "lucide-react";

import { useStartWhatsappLink } from "@/shared/api/interfaces";
import { GhostButton, PrimaryButton } from "@/shared/components/settings-atoms";
import { FormError } from "./FormError";
import { PairingCode } from "./PairingCode";
import { Steps } from "./Steps";

import type { useWhatsappLink } from "@/shared/api/interfaces";

const SCAN = [
  "Open WhatsApp on the phone that has this number.",
  "Go to Settings → Linked devices → Link a device.",
  "Point the phone at this code.",
];

const UNREACHABLE = { 3110003: "Couldn't reach WhatsApp — try again." };

/* Step two of linking a number: where linking stands, read every 2 s while a
   code is on screen. Reading never starts anything. A link that is not running —
   interrupted by a bridge restart, or ended because the codes ran out or the
   phone logged it out — says why (the row's error when the service recorded one,
   otherwise that the code expired) and offers "Show a new code", the one thing
   that starts a new session. */
export function WhatsappLink({ interfaceId, link, ended, agentName }: {
  interfaceId: string;
  link: ReturnType<typeof useWhatsappLink>;
  /** The row's error, if the service recorded why the link ended. */
  ended: string | null;
  agentName: string;
}) {
  const [byPhone, setByPhone] = useState(false);
  const newCode = useStartWhatsappLink();

  if (link.isPending) {
    return <p className="m-0 py-8 text-center text-[13px] shimmer-text">Starting the connection…</p>;
  }

  // A failed read wins over the last good one: the code on screen may be stale,
  // and the next poll brings it back if the bridge recovers.
  if (link.isError || !link.data) {
    return (
      <div className="space-y-3">
        <FormError error={link.error} words={UNREACHABLE} />
        <GhostButton onClick={() => link.refetch()}>Try again</GhostButton>
      </div>
    );
  }

  const { state, qr_image, phone } = link.data;

  if (state === "linked") {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <div className="w-10 h-10 rounded-[12px] bg-[var(--success-bg)] ring-1 ring-[var(--success-ring)] flex items-center justify-center">
          <Check className="w-[18px] h-[18px] text-[var(--success)]" strokeWidth={2} />
        </div>
        <div className="text-[15px] font-medium tracking-[-0.005em]">
          Connected · <span className="font-mono tabular-nums">{phone}</span>
        </div>
        <div className="text-[13px] text-[var(--text-tertiary)]">{agentName} answers messages to this number now.</div>
      </div>
    );
  }

  if (state === "not_started" || state === "logged_out") {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="text-[15px] font-medium tracking-[-0.005em]">{ended ?? "The code expired."}</div>
        <PrimaryButton onClick={() => newCode.mutate(interfaceId)} disabled={newCode.isPending}>
          {newCode.isPending ? "Getting a new code…" : "Show a new code"}
        </PrimaryButton>
        <FormError error={newCode.error} words={UNREACHABLE} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {byPhone ? (
        <PairingCode interfaceId={interfaceId} />
      ) : (
        <>
          {/* A session that has just started has no code yet: WhatsApp sends the
              first one a moment later, and the next read brings it. */}
          {qr_image ? (
            <img
              src={qr_image}
              alt="Code to scan with WhatsApp"
              className="self-center w-[180px] h-[180px] rounded-[10px] ring-1 ring-[var(--hairline)]"
            />
          ) : (
            <div className="self-center w-[180px] h-[180px] rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
              <span className="text-[13px] shimmer-text">Getting a code…</span>
            </div>
          )}
          <Steps items={SCAN} />
        </>
      )}
      <p className="m-0 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">
        Once linked, the phone lists it under Linked devices as “Definable (Ubuntu)”.
      </p>
      <button
        type="button"
        onClick={() => setByPhone(!byPhone)}
        className="press self-center text-[13px] text-[var(--text-secondary)] underline underline-offset-2 hover:text-[var(--text-primary)]"
      >
        {byPhone ? "Scan a code instead" : "Link with phone number instead"}
      </button>
    </div>
  );
}
